import { getStateStore } from '../../stores';
import { getIpcFunctions } from '../ipcInterface';
import { dropAllGamepads, initializeAllGamepads } from './gamepadEvents';
import { TWITCH_INVITE_LINK_INTERVAL } from '../../../config.json';
import { discordSettingsState, hostMatchmakingState, matchmakerState, twitchSettingsState, steamCredentialsState, adModeState } from '../../stores/shared';
import { USE_LEGACY_PLAYJECTOR_EVENTS, USE_MESSAGE_ROUTER } from '../../../config/featureFlags';
import {
  discordSendEndAnnouncement,
  discordSendStartAnnouncement,
  twitchSendEndAnnouncement,
  twitchSendNotify,
  twitchSendReminder,
  twitchSendStartAnnouncement,
} from '../apiV2';
import { generateQRCode, generateQRCodePng } from '@playcastdotio/Qrcode';
import pcLogo from '../../../../assets/playcast_logo-96x96.png';
import shareGamepadLogo from '../../../../assets/icons/share-gamepad.svg';
import { generators, websocket, MatchmakingMessageType } from '@playcastdotio/Messaging';
import { ipcSend } from '../../messaging/protocols/ipc';
import { playjectorStartPavilion, playjectorStopPavilion, sendHostReady, sendMatchmakerHostConfig } from '../managers/matchmaker';
import { notifyStreamEnded } from '../managers/notifications';
import { sendMessage } from '../realtime';
import constants from '@playcastdotio/GlobalConfig';
import config from '../../../config.json';
// import { invalidateInvitation, setInvitation } from '../managers/invitation';

// Session timeout variables
let sessionTimeoutTimer: NodeJS.Timeout | undefined;

const convertImageToBase64 = async (imageUrl: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }
      ctx.drawImage(img, 0, 0);
      try {
        const dataUrl = canvas.toDataURL('image/png');
        resolve(dataUrl);
      } catch (error) {
        reject(error);
      }
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = imageUrl;
  });
};

const state = getStateStore('ipc');
let twitchState = twitchSettingsState.currentState;
let discordState = discordSettingsState.currentState;
let startingTimeout: NodeJS.Timeout;
let twitchNotifyInterval: NodeJS.Timeout;
let twitchInfoInterval: NodeJS.Timeout;
let twitchStaggerTimeout: NodeJS.Timeout;

let cachedPlayerCount = 0;
// Check whether to start or clear the session timeout
export const checkSessionTimeout = (playerCount: number) => {
  // If the player count hasn't changed, don't do anything
  if (playerCount === cachedPlayerCount) {
    return;
  }
  cachedPlayerCount = playerCount;

  // Clear any existing timeout timer
  if (sessionTimeoutTimer) {
    clearTimeout(sessionTimeoutTimer);
    sessionTimeoutTimer = undefined;
  }

  // Check if we're in pavilion edition
  const isPavilion = state.hostState.currentState.edition === 'pavilion';

  // Skip timeout for ad mode (handled immediately in playerEvents.ts)
  const isAdMode = adModeState.currentState.enabled;
  if (isAdMode) {
    return;
  }

  // If we're in pavilion and player count is 0, start the timeout
  if (isPavilion && playerCount === 0 && state.hostState.currentState.status === 'streaming') {
    console.warn('[Host] No players connected in pavilion mode, starting session timeout');

    sessionTimeoutTimer = setTimeout(() => {
      console.warn('[Host] Session timeout reached with no players, stopping session');
      getIpcFunctions().stopStream('host:pavilion:no_players_timeout'); // Remotely click button, basically.
    }, config.PAVILION_NO_PLAYERS_TIMEOUT);
  }
};

const clearTwitchIntervals = () => {
  return;
  console.warn('[TWITCH] clearing intervals');
  clearInterval(twitchNotifyInterval);
  clearInterval(twitchInfoInterval);
  clearTimeout(twitchStaggerTimeout);
};

const setupTwitchIntervals = () => {
  console.warn('[TWITCH] setting up intervals');
  if (twitchState.isSendStreamUrlReminder) {
    if (twitchNotifyInterval) {
      return;
    }
    console.warn('[TWITCH] setting up reminder interval');
    twitchNotifyInterval = setInterval(() => {
      console.warn('[TWITCH] sending reminder');
      if (state.invitationStateV2.currentState.id) {
        const type = state.invitationStateV2.currentState.type;
        const streamUrl = `${type === 'cafe' ? constants.PAVILION_PLAYER_BASE_URL : constants.PRIME_PLAYER_BASE_URL}j/${state.invitationStateV2.currentState.id}`;
        twitchSendReminder(streamUrl);
      }
    }, TWITCH_INVITE_LINK_INTERVAL);
  }

  if (twitchState.isSendPlaycastInfo) {
    if (twitchInfoInterval) {
      return;
    }
    twitchStaggerTimeout = setTimeout(() => {
      console.warn('[TWITCH] setting up info timeout');
      twitchInfoInterval = setInterval(() => {
        if (state.invitationStateV2.currentState.id) {
          twitchSendNotify();
        }
      }, TWITCH_INVITE_LINK_INTERVAL);
    }, TWITCH_INVITE_LINK_INTERVAL / 2);
  }
};

let cachedHostState = state.hostState.currentState;
let cachedMatchmakeState = state.hostMatchmakingState.currentState;

export const initializeHostEvents = () => {
  state.hostState.addListener((status) => {
    const isStreaming = status.status !== 'idle';
    const wasStreaming = cachedHostState.status !== 'idle';

    if (isStreaming) {
      state.qrCodeState.currentState.defaultPlayerState.content = state.invitationStateV2.currentState.id;
      state.qrCodeState.updateState(state.qrCodeState.currentState);

      if (!wasStreaming) {
        if (twitchState.isSendStartAnnouncement) twitchSendStartAnnouncement();
        if (discordState.isDiscordBotEnabled) discordSendStartAnnouncement();
        clearTwitchIntervals();
        setupTwitchIntervals();
      }
    } else {
      clearTwitchIntervals();
      if (wasStreaming) twitchSendEndAnnouncement();
      if (wasStreaming && discordState.isDiscordBotEnabled) discordSendEndAnnouncement();

      // Clear session timeout when leaving streaming state
      if (sessionTimeoutTimer) {
        clearTimeout(sessionTimeoutTimer);
        sessionTimeoutTimer = undefined;
      }
    }

    if (status.status === 'stopping') {
      //dropAllGamepads();
      if (state.hostState.currentState.edition === 'pavilion') {
        notifyStreamEnded();
        // Only terminate pavilion process if NOT in ad mode
        if (!state.adModeState.currentState.enabled) {
          playjectorStopPavilion();
        }
        if (state.matchmakerState.currentState.stateDetails?.currentMatchInvitationId) {
          sendMessage(
            websocket.generators.matchmaking(MatchmakingMessageType.MATCHMAKER_UPDATE_INVITATION, {
              invitationId: state.matchmakerState.currentState.stateDetails.currentMatchInvitationId,
              invalidationReason: 'Host stopped stream',
              invalid: true,
            }),
          );
        }
        matchmakerState.reset();
      }
    }
    if (status.status === 'starting') {
      //initializeAllGamepads();
      if (localStorage.getItem('autoStream') !== 'true') {
        // console.warn('INVALIDATING');
        // invalidateInvitation();
      } else {
        // setInvitation({ id: state.invitationStateV2.currentState.id, invalid: false });
      }
      startingTimeout = setTimeout(() => {
        if (state.hostState.currentState.status === 'starting') getIpcFunctions().stopStream('host:init:timeout');
      }, 7000);
    }
    if (status.status === 'streaming') {
      clearTimeout(startingTimeout);
      if (cachedHostState.status !== 'streaming') {
        if (state.matchmakerState.currentState.stateDetails?.currentMatchInvitationId) {
          sendHostReady();
        }

        // Check session timeout when entering streaming state
        const playerCount = Object.keys(state.playerState.currentState.players).length;
        checkSessionTimeout(playerCount);
      }

      // // Make sure the current invitation is valid when streaming
      // if (state.invitationStateV2.currentState.id && state.invitationStateV2.currentState.invalid) {
      //   // Re-validate the invitation when streaming starts/continues
      //   setInvitation({
      //     ...state.invitationStateV2.currentState,
      //     invalid: false,
      //   });
      // }
    }

    // Update the Playjector with the current host edition
    const edition = status.edition;
    const lastEdition = cachedHostState.edition;
    if (edition !== lastEdition) {
      if (edition === 'standard') {
        console.log('[Host] Entering standard mode');
        // Clear invitationv2, and clear localstorage
        state.invitationStateV2.reset();
        localStorage.removeItem('currentInvitation');
        // Deregister from matchmaker
        hostMatchmakingState.updateState({
          ...hostMatchmakingState.currentState,
          globalMatchmakingToggle: false,
          matchmakeable: false,
        });
        // TODO-STRANGLER-FIG: Legacy HOST_CONFIG send - remove when PavilionCoordinator fully owns this
        if (!USE_MESSAGE_ROUTER) {
          sendMatchmakerHostConfig().catch((err) => {
            console.error('[Host] Failed to send host config after edition change:', err);
          });
        }
      } else if (edition === 'pavilion') {
        console.log('[Host] Entering cafe mode');
        // Clear invitationv2, and clear localstorage
        state.invitationStateV2.reset();
        localStorage.removeItem('currentInvitation');
        // Ensure globalMatchmakingToggle is true
        hostMatchmakingState.currentState.globalMatchmakingToggle = true;
        // Set audio source to game capture
        // getIpcFunctions().setDesktopAudio('gameCapture');
        getIpcFunctions().setDesktopAudio('desktop');
      }

      let steamGuestLogin = false;
      if (state.invitationStateV2.currentState.type === 'cafe') {
        steamGuestLogin = state.invitationStateV2.currentState.manifest.useSteamGuestLogin ?? false;
      }

      // TODO-STRANGLER-FIG: Legacy IPC send - remove when PavilionCoordinator fully owns this
      if (USE_LEGACY_PLAYJECTOR_EVENTS && state.hostState.currentState.edition) {
        ipcSend(
          generators.host.hostModeChanged(
            edition ?? 'standard',
            state.hostMatchmakingState.currentState.matchmakeable,
            steamGuestLogin,
            state.hostMatchmakingState.currentState.guestAccount,
            steamCredentialsState.currentState,
            state.authState.currentState.hostAuthId,
          ),
        );
      }
    }

    cachedHostState = status;
  });

  // Update the Playjector with the current matchmaking state
  state.hostMatchmakingState.addListener((status) => {
    const matchmakeable = status.matchmakeable;

    if (matchmakeable !== cachedMatchmakeState.matchmakeable) {
      if (matchmakeable) {
        console.log('[Host] Machine can now be matchmade');
      } else {
        console.log('[Host] Machine cannot be matchmade');
      }

      let steamGuestLogin = false;
      if (state.invitationStateV2.currentState.type === 'cafe') {
        steamGuestLogin = state.invitationStateV2.currentState.manifest.useSteamGuestLogin ?? false;
      }

      // TODO-STRANGLER-FIG: Legacy IPC send - remove when PavilionCoordinator fully owns this
      if (USE_LEGACY_PLAYJECTOR_EVENTS && state.hostState.currentState.edition) {
        ipcSend(
          generators.host.hostModeChanged(
            state.hostState.currentState.edition ?? 'standard',
            matchmakeable,
            steamGuestLogin,
            state.hostMatchmakingState.currentState.guestAccount,
            steamCredentialsState.currentState,
            state.authState.currentState.hostAuthId,
          ),
        );
      }
    }

    cachedMatchmakeState = status;
  });

  let cachedId = '';
  state.invitationStateV2.addListener(async (invitation) => {
    // console.warn('Invitation state changed:', invitation.id, cachedId, state.hostState.currentState.status);
    if (invitation.id !== '') {
      if (invitation.id !== cachedId) {
        cachedId = invitation.id;

        const type = invitation.type;
        const streamUrl = `${type === 'cafe' ? constants.PAVILION_PLAYER_BASE_URL : constants.PRIME_PLAYER_BASE_URL}j/${invitation.id}`;
        const qrCode = await generateQRCodePng(streamUrl, { logo: pcLogo });
        if (qrCode) {
          state.qrCodeBase64State.updateState(qrCode);
        } else {
          console.error('Failed to generate QR code');
        }
      }
    }
  });

  twitchSettingsState.addListener((status) => {
    twitchState = status;
    if (state.hostState.currentState.status === 'streaming') {
      clearTwitchIntervals();
      setupTwitchIntervals();
    } else {
      clearTwitchIntervals();
    }
  });

  discordSettingsState.addListener((status) => {
    discordState = status;
  });

  state.gameClipConfigState.addListener((encoder) => {
    const config = {
      ...state.gameClipConfigState.currentState,
      enabled: encoder.enabled,
      recordHotkey: encoder.recordHotkey,
      bufferDurationSeconds: encoder.bufferDurationSeconds,
      cameraName: encoder.cameraName,
      directoryPath: encoder.directoryPath,
    };
    console.log('[Host] Sending encoder config:', config);
    ipcSend(generators.encoder.setEncoderConfig(state.authState.currentState.hostAuthId, config));
  });

  // TODO-STRANGLER-FIG: Legacy listener - remove when PavilionCoordinator fully owns this
  if (USE_LEGACY_PLAYJECTOR_EVENTS) {
    steamCredentialsState.addListener((credentials) => {
      let steamGuestLogin = false;
      if (state.invitationStateV2.currentState.type === 'cafe') {
        steamGuestLogin = state.invitationStateV2.currentState.manifest.useSteamGuestLogin ?? false;
      }

      if (state.hostState.currentState.edition) {
        ipcSend(
          generators.host.hostModeChanged(
            state.hostState.currentState.edition ?? 'standard',
            state.hostMatchmakingState.currentState.matchmakeable,
            steamGuestLogin,
            state.hostMatchmakingState.currentState.guestAccount,
            credentials,
            state.authState.currentState.hostAuthId,
          ),
        );
      }
    });
  }
};
